import { createContext, useContext, useEffect, useState, ReactNode, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";

const STORAGE_KEY = "a11y-mode";

interface AccessibilityContextValue {
  enabled: boolean;
  setEnabled: (next: boolean) => Promise<void>;
  loading: boolean;
}

const AccessibilityContext = createContext<AccessibilityContextValue | null>(null);

function readCached(): boolean {
  try {
    return localStorage.getItem(STORAGE_KEY) === "1";
  } catch {
    return false;
  }
}

function applyAttribute(enabled: boolean) {
  if (typeof document === "undefined") return;
  if (enabled) {
    document.documentElement.setAttribute("data-a11y", "on");
  } else {
    document.documentElement.removeAttribute("data-a11y");
  }
}

export function AccessibilityProvider({ children }: { children: ReactNode }) {
  const { user, realUser } = useAuth();
  const userId = realUser?.id ?? user?.id ?? null;
  const [enabled, setEnabledState] = useState<boolean>(() => {
    const v = readCached();
    applyAttribute(v);
    return v;
  });
  const [loading, setLoading] = useState(false);

  // Load from profile when user becomes available
  useEffect(() => {
    if (!userId) return;
    let cancelled = false;
    (async () => {
      const { data, error } = await supabase
        .from("profiles")
        .select("accessibility_mode")
        .eq("id", userId)
        .maybeSingle();
      if (cancelled || error || !data) return;
      const next = data.accessibility_mode === true;
      setEnabledState(next);
      applyAttribute(next);
      try {
        localStorage.setItem(STORAGE_KEY, next ? "1" : "0");
      } catch {
        /* ignore */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [userId]);

  const setEnabled = useCallback(
    async (next: boolean) => {
      setEnabledState(next);
      applyAttribute(next);
      try {
        localStorage.setItem(STORAGE_KEY, next ? "1" : "0");
      } catch {
        /* ignore */
      }
      if (!userId) return;
      setLoading(true);
      const { error } = await supabase
        .from("profiles")
        .update({ accessibility_mode: next })
        .eq("id", userId);
      setLoading(false);
      if (error) {
        console.error("Failed to persist accessibility_mode:", error);
        throw error;
      }
    },
    [userId]
  );

  return (
    <AccessibilityContext.Provider value={{ enabled, setEnabled, loading }}>
      {children}
    </AccessibilityContext.Provider>
  );
}

export function useAccessibility(): AccessibilityContextValue {
  const ctx = useContext(AccessibilityContext);
  if (!ctx) throw new Error("useAccessibility must be used within an AccessibilityProvider");
  return ctx;
}
